import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useCurrentOrg } from '../orgs/useCurrentOrg';
import { Card, CardBody } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { listAudit, type AuditRecord } from './api';

const EVENT_TYPES = [
  '',
  'wallet.created',
  'recovery.initiated',
  'recovery.verified',
  'recovery.completed',
  'api_key.created',
  'api_key.revoked',
  'org.auth_updated',
  'org.settings_updated',
  'org.recovery_mode_updated',
  'webhook.updated',
  'webhook.removed',
] as const;

const PAGE_SIZE = 50;

export function AuditLogPage() {
  const { org } = useCurrentOrg();
  const [eventType, setEventType] = useState<string>('');
  const [success, setSuccess] = useState<'' | 'true' | 'false'>('');
  const [offset, setOffset] = useState(0);

  const filters = {
    event_type: eventType || undefined,
    success: success === '' ? undefined : success === 'true',
    limit: PAGE_SIZE,
    offset,
  };

  const audit = useQuery({
    queryKey: ['audit', org?.id, filters],
    queryFn: () => listAudit(org!.id, filters),
    enabled: !!org,
    placeholderData: (prev) => prev,
  });

  if (!org) return null;

  const items = audit.data?.items ?? [];
  const total = audit.data?.total ?? 0;
  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">Audit log</h2>
        <p className="mt-1 text-sm text-ink-secondary">
          Append-only stream of security-sensitive events: wallet creation, recovery, API key changes,
          settings updates.
        </p>
      </div>

      <Card>
        <CardBody>
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-ink-primary">Event</label>
              <select
                value={eventType}
                onChange={(e) => {
                  setEventType(e.target.value);
                  setOffset(0);
                }}
                className="block h-10 w-60 rounded-md border border-line bg-white px-3 text-sm focus:border-ink-primary focus:outline-none focus:ring-1 focus:ring-ink-primary"
              >
                {EVENT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t === '' ? 'All events' : t}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-ink-primary">Outcome</label>
              <select
                value={success}
                onChange={(e) => {
                  setSuccess(e.target.value as '' | 'true' | 'false');
                  setOffset(0);
                }}
                className="block h-10 w-40 rounded-md border border-line bg-white px-3 text-sm focus:border-ink-primary focus:outline-none focus:ring-1 focus:ring-ink-primary"
              >
                <option value="">All</option>
                <option value="true">Success</option>
                <option value="false">Failure</option>
              </select>
            </div>
            <div className="ml-auto text-sm text-ink-tertiary">
              {audit.isLoading
                ? 'Loading…'
                : `${total.toLocaleString()} event${total === 1 ? '' : 's'}`}
            </div>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardBody className="!p-0">
          {items.length === 0 && !audit.isLoading && (
            <p className="px-6 py-8 text-center text-sm text-ink-tertiary">No events match these filters.</p>
          )}
          {items.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full table-fixed text-sm">
                <colgroup>
                  <col className="w-[110px]" />
                  <col className="w-[200px]" />
                  <col className="w-[160px]" />
                  <col />
                  <col className="w-[100px]" />
                </colgroup>
                <thead className="text-xs uppercase tracking-wider text-ink-tertiary">
                  <tr className="border-b border-line-soft">
                    <th className="px-6 py-3 text-left font-medium">When</th>
                    <th className="px-6 py-3 text-left font-medium">Event</th>
                    <th className="px-6 py-3 text-left font-medium">Actor</th>
                    <th className="px-6 py-3 text-left font-medium">Detail</th>
                    <th className="px-6 py-3 text-right font-medium">Outcome</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line-soft">
                  {items.map((rec) => (
                    <Row key={rec.id} rec={rec} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      <div className="flex items-center justify-between text-sm text-ink-secondary">
        <span>
          Page {page} of {lastPage}
        </span>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          >
            Previous
          </Button>
          <Button
            variant="secondary"
            disabled={offset + PAGE_SIZE >= total}
            onClick={() => setOffset(offset + PAGE_SIZE)}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}

function Row({ rec }: { rec: AuditRecord }) {
  const ts = new Date(rec.timestamp);
  return (
    <tr>
      <td className="whitespace-nowrap px-6 py-3 text-ink-secondary">
        <span className="block">{ts.toLocaleDateString()}</span>
        <span className="block text-xs text-ink-tertiary">{ts.toLocaleTimeString()}</span>
      </td>
      <td className="whitespace-nowrap px-6 py-3 font-mono text-xs">{rec.event_type}</td>
      <td className="whitespace-nowrap px-6 py-3 text-ink-secondary">
        <span className="block text-xs uppercase tracking-eyebrow text-ink-tertiary">{rec.actor_type}</span>
        {rec.actor_id && (
          <span className="block font-mono text-xs" title={rec.actor_id}>
            {shortId(rec.actor_id)}
          </span>
        )}
      </td>
      <td className="px-6 py-3">
        <Payload payload={rec.payload} />
      </td>
      <td className="whitespace-nowrap px-6 py-3 text-right">
        {rec.success ? (
          <span className="rounded-full bg-good/[0.08] px-2 py-0.5 text-xs font-medium text-good">ok</span>
        ) : (
          <span className="rounded-full bg-bad/[0.08] px-2 py-0.5 text-xs font-medium text-bad">
            {rec.error_code || 'failed'}
          </span>
        )}
      </td>
    </tr>
  );
}

function Payload({ payload }: { payload: Record<string, unknown> | null }) {
  if (!payload || Object.keys(payload).length === 0) return <span className="text-ink-tertiary">—</span>;
  return (
    <div className="space-y-0.5">
      {Object.entries(payload).map(([k, v]) => {
        const raw = String(v);
        const short = looksLikeId(raw) ? shortId(raw) : raw;
        return (
          <div key={k} className="flex gap-2 font-mono text-xs">
            <span className="shrink-0 text-ink-tertiary">{k}</span>
            <span className="min-w-0 truncate text-ink-primary" title={raw}>
              {short}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// UUIDs, hex addresses, sha-ish blobs — anything long and opaque.
function looksLikeId(s: string): boolean {
  if (s.length < 16) return false;
  if (/^[0-9a-fA-F-]{16,}$/.test(s)) return true;
  if (/^0x[0-9a-fA-F]{8,}$/.test(s)) return true;
  return false;
}

function shortId(id: string): string {
  if (id.length <= 12) return id;
  return `${id.slice(0, 8)}…`;
}
